import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Icons } from './ui/Icons';

interface AudioPlayerProps {
  text: string;
  segmentType: 'text' | 'verse' | 'quiz' | 'image' | 'video' | 'image-text' | 'question' | 'cta';
  audioUrl?: string; // Pre-generated audio file URL (takes priority over TTS)
  className?: string;
  autoPlay?: boolean;
  onPlaybackStart?: () => void;
  onPlaybackEnd?: () => void;
  onPauseChange?: (isPaused: boolean) => void;
}

export interface AudioPlayerRef {
  resumePlayback: () => void;
  pausePlayback: () => void;
}

export const AudioPlayer = forwardRef<AudioPlayerRef, AudioPlayerProps>(({
  text,
  segmentType,
  audioUrl,
  className = '',
  autoPlay = false,
  onPlaybackStart,
  onPlaybackEnd,
  onPauseChange
}, ref) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const intervalRef = useRef<number | null>(null);
  const textChunksRef = useRef<string[]>([]);
  const currentChunkIndexRef = useRef<number>(0);

  // Check if speech synthesis is supported
  const isSpeechSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  // Use pre-generated audio if available
  const usePreGeneratedAudio = !!audioUrl;

  // Expose resumePlayback and pausePlayback methods to parent component
  useImperativeHandle(ref, () => ({
    resumePlayback: () => {
      if (isPaused) {
        if (usePreGeneratedAudio && audioRef.current) {
          audioRef.current.play();
          setIsPaused(false);
          if (onPauseChange) onPauseChange(false);
        } else if (isSpeechSupported) {
          window.speechSynthesis.resume();
          setIsPaused(false);
          if (onPauseChange) onPauseChange(false);

          // Resume progress tracking
          intervalRef.current = window.setInterval(() => {
            setCurrentTime(prev => {
              const next = prev + 0.1;
              return next >= duration ? duration : next;
            });
          }, 100);
        }
      }
    },
    pausePlayback: () => {
      if (isPlaying && !isPaused) {
        if (usePreGeneratedAudio && audioRef.current) {
          audioRef.current.pause();
          setIsPaused(true);
          if (onPauseChange) onPauseChange(true);
        } else if (isSpeechSupported) {
          window.speechSynthesis.pause();
          setIsPaused(true);
          if (onPauseChange) onPauseChange(true);

          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }
    }
  }), [isPaused, isPlaying, usePreGeneratedAudio, isSpeechSupported, duration, onPauseChange]);

  useEffect(() => {
    // Clean up on unmount
    return () => {
      if (utteranceRef.current) {
        window.speechSynthesis.cancel();
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Stop and reset audio when text or audioUrl changes (switching segments)
  useEffect(() => {
    // Stop any currently playing audio
    handleStop();

    // Reset audio ref when audioUrl changes to prevent old audio from playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // If autoPlay is enabled, start playing the new segment
    if (autoPlay && text && isSpeechSupported) {
      handlePlay();
    }
  }, [text, audioUrl]);

  // Handle autoPlay toggle - start playing when autoPlay is enabled
  useEffect(() => {
    if (autoPlay && text && isSpeechSupported && !isPlaying) {
      handlePlay();
    }
  }, [autoPlay]);

  // Split text into chunks (Chrome has a ~32KB character limit)
  const splitTextIntoChunks = (text: string, maxLength: number = 200): string[] => {
    if (text.length <= maxLength) {
      return [text];
    }

    const chunks: string[] = [];
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
    let currentChunk = '';

    for (const sentence of sentences) {
      if ((currentChunk + sentence).length > maxLength) {
        if (currentChunk) {
          chunks.push(currentChunk.trim());
          currentChunk = sentence;
        } else {
          // Sentence itself is too long, split by words
          const words = sentence.split(' ');
          for (const word of words) {
            if ((currentChunk + ' ' + word).length > maxLength) {
              chunks.push(currentChunk.trim());
              currentChunk = word;
            } else {
              currentChunk += (currentChunk ? ' ' : '') + word;
            }
          }
        }
      } else {
        currentChunk += (currentChunk ? ' ' : '') + sentence;
      }
    }

    if (currentChunk) {
      chunks.push(currentChunk.trim());
    }

    return chunks;
  };

  // Estimate duration based on text length (rough approximation)
  useEffect(() => {
    if (text) {
      // Average speaking rate is ~150 words per minute
      const wordCount = text.split(/\s+/).length;
      const estimatedDuration = (wordCount / 150) * 60 / playbackRate;
      setDuration(estimatedDuration);
    }
  }, [text, playbackRate]);

  const handlePlay = () => {
    if (usePreGeneratedAudio && audioUrl) {
      // Use pre-generated audio file
      if (!audioRef.current) {
        audioRef.current = new Audio(audioUrl);
        audioRef.current.playbackRate = playbackRate;

        // Set up event listeners
        audioRef.current.addEventListener('loadedmetadata', () => {
          setDuration(audioRef.current!.duration);
        });

        audioRef.current.addEventListener('timeupdate', () => {
          setCurrentTime(audioRef.current!.currentTime);
        });

        audioRef.current.addEventListener('ended', () => {
          setIsPlaying(false);
          setIsPaused(false);
          setCurrentTime(0);
          if (onPauseChange) onPauseChange(false);
          if (onPlaybackEnd) {
            onPlaybackEnd();
          }
        });

        audioRef.current.addEventListener('error', (e) => {
          console.error('Audio playback error:', e);
          setIsPlaying(false);
          setIsPaused(false);
          setIsLoading(false);
        });
      }

      setIsLoading(true);
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          setIsPaused(false);
          setIsLoading(false);
          if (onPlaybackStart) {
            onPlaybackStart();
          }
        })
        .catch(err => {
          console.error('Play error:', err);
          setIsLoading(false);
        });

    } else {
      // Use browser TTS
      if (!isSpeechSupported || !text) {
        console.log('Speech not supported or no text:', { isSpeechSupported, text: text?.substring(0, 50) });
        return;
      }

      // Don't read out content for image, video, cta, or question segments (unless pre-generated audio is used)
      // Also skip if text looks like a URL
      if (segmentType === 'image' || segmentType === 'video' || segmentType === 'cta' || segmentType === 'question' || text.trim().match(/^https?:\/\//)) {
        return;
      }

      // Stop any ongoing speech
      window.speechSynthesis.cancel();

      // Load voices if not loaded yet (important for some browsers)
      let voices = window.speechSynthesis.getVoices();

      const speakNextChunk = () => {
        if (currentChunkIndexRef.current >= textChunksRef.current.length) {
          // All chunks spoken
          setIsPlaying(false);
          setIsPaused(false);
          setCurrentTime(duration);
          if (onPauseChange) onPauseChange(false);

          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          if (onPlaybackEnd) {
            onPlaybackEnd();
          }
          return;
        }

        const chunkText = textChunksRef.current[currentChunkIndexRef.current];
        const utterance = new SpeechSynthesisUtterance(chunkText);
        utteranceRef.current = utterance;

        // Set voice properties
        utterance.rate = playbackRate;
        utterance.pitch = 1.0;
        utterance.volume = 1.0;

        // Try to use a more natural voice if available
        voices = window.speechSynthesis.getVoices();
        const preferredVoice = voices.find(voice =>
          voice.lang.startsWith('en') && (voice.name.includes('Natural') || voice.name.includes('Google'))
        ) || voices.find(voice => voice.lang.startsWith('en'));

        if (preferredVoice) {
          utterance.voice = preferredVoice;
        }

        // Event handlers
        utterance.onstart = () => {
          if (currentChunkIndexRef.current === 0) {
            setIsPlaying(true);
            setIsPaused(false);
            setIsLoading(false);
            setCurrentTime(0);

            if (onPlaybackStart) {
              onPlaybackStart();
            }

            // Start progress tracking
            intervalRef.current = window.setInterval(() => {
              setCurrentTime(prev => {
                const next = prev + 0.1;
                return next >= duration ? duration : next;
              });
            }, 100);
          }
        };

        utterance.onend = () => {
          currentChunkIndexRef.current++;
          speakNextChunk();
        };

        utterance.onerror = (event) => {
          console.error('Speech synthesis error:', event);
          setIsPlaying(false);
          setIsPaused(false);
          setIsLoading(false);

          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        };

        // Speak this chunk
        window.speechSynthesis.speak(utterance);
      };

      const initSpeech = () => {
        // Split text into manageable chunks
        textChunksRef.current = splitTextIntoChunks(text);
        currentChunkIndexRef.current = 0;

        // Start speaking
        setIsLoading(true);
        console.log(`Starting speech synthesis with ${textChunksRef.current.length} chunks`);
        speakNextChunk();
      };

      // If voices are not loaded yet, wait for them
      if (voices.length === 0) {
        window.speechSynthesis.addEventListener('voiceschanged', initSpeech, { once: true });
        // Fallback in case voiceschanged doesn't fire
        setTimeout(initSpeech, 100);
      } else {
        initSpeech();
      }
    }
  };

  const handlePause = () => {
    if (usePreGeneratedAudio && audioRef.current) {
      // Handle pre-generated audio
      if (isPlaying && !isPaused) {
        audioRef.current.pause();
        setIsPaused(true);
        if (onPauseChange) onPauseChange(true);
      } else if (isPaused) {
        audioRef.current.play();
        setIsPaused(false);
        if (onPauseChange) onPauseChange(false);
      }
    } else {
      // Handle browser TTS
      if (!isSpeechSupported) return;

      if (isPlaying && !isPaused) {
        window.speechSynthesis.pause();
        setIsPaused(true);
        if (onPauseChange) onPauseChange(true);

        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else if (isPaused) {
        window.speechSynthesis.resume();
        setIsPaused(false);
        if (onPauseChange) onPauseChange(false);

        // Resume progress tracking
        intervalRef.current = window.setInterval(() => {
          setCurrentTime(prev => {
            const next = prev + 0.1;
            return next >= duration ? duration : next;
          });
        }, 100);
      }
    }
  };

  const handleStop = () => {
    if (usePreGeneratedAudio && audioRef.current) {
      // Handle pre-generated audio
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentTime(0);
      if (onPauseChange) onPauseChange(false);
    } else {
      // Handle browser TTS
      if (!isSpeechSupported) return;

      window.speechSynthesis.cancel();
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentTime(0);
      currentChunkIndexRef.current = 0;
      if (onPauseChange) onPauseChange(false);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  };

  const handleSpeedChange = () => {
    const speeds = [0.75, 1.0, 1.25, 1.5, 2.0];
    const currentIndex = speeds.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % speeds.length;
    const newSpeed = speeds[nextIndex];
    setPlaybackRate(newSpeed);

    // Update speed for pre-generated audio
    if (audioRef.current) {
      audioRef.current.playbackRate = newSpeed;
    }

    // If currently playing with TTS, restart with new speed
    if (isPlaying && !usePreGeneratedAudio) {
      handlePlay();
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Don't render for non-text segments or if speech not supported
  if (!isSpeechSupported) {
    return null;
  }

  // Only show for text, verse, quiz, and image-text segments
  if (segmentType !== 'text' && segmentType !== 'verse' && segmentType !== 'quiz' && segmentType !== 'image-text') {
    return null;
  }

  // Don't show if there's no text to read
  if (!text || text.trim().length === 0) {
    return null;
  }

  // Only show controls when playing
  if (!isPlaying && !isPaused) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Pause/Resume Button */}
      <button
        onClick={handlePause}
        className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white hover:bg-blue-700 transition-colors shadow-sm"
        title={isPaused ? "Resume" : "Pause"}
      >
        {isPaused ? <Icons.Play className="w-4 h-4" /> : <Icons.Pause className="w-4 h-4" />}
      </button>

      {/* Progress Bar */}
      <div className="flex items-center gap-2 min-w-[100px]">
        <div className="w-20 h-1 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-100"
            style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
          />
        </div>
        <span className="text-xs text-gray-500 font-mono whitespace-nowrap">
          {formatTime(currentTime)}/{formatTime(duration)}
        </span>
      </div>

      {/* Speed Control */}
      <button
        onClick={handleSpeedChange}
        className="px-2 py-0.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
        title="Playback Speed"
      >
        {playbackRate}x
      </button>

      {/* Stop Button */}
      <button
        onClick={handleStop}
        className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
        title="Stop"
      >
        <Icons.X className="w-4 h-4" />
      </button>
    </div>
  );
});

AudioPlayer.displayName = 'AudioPlayer';
